import type { ApiResource } from '../schema';

export const resource: ApiResource = {
  id: 'docs',
  slug: 'docs',
  name: 'Documentation & Knowledge Base',
  description: 'Per-client IT documentation (folders, pages, revisions), the general document library, and the knowledge-base article catalog that powers ticket-deflection suggestions.',
  icon: 'ArticleOutlined',
  endpoints: [
    {
      slug: 'itdocs-list-pages',
      method: 'GET',
      path: '/api/itdocs/pages',
      summary: 'List IT documentation pages',
      description: 'Returns IT documentation pages on the root tenant\'s own store. Filter by clientTenantId (empty string is platform-wide), folderId, isTemplate, or a free-text q that matches title/body/category/tags. Pinned pages sort first, then most recently updated.',
      auth: 'session',
      params: [
        { name: 'clientTenantId', in: 'query', type: 'string', description: 'Scope to one client tenant, or "" for platform-wide pages.' },
        { name: 'folderId', in: 'query', type: 'string', description: 'Scope to one folder.' },
        { name: 'q', in: 'query', type: 'string', description: 'Free-text search across title, body, category, and tags.' },
      ],
      responseExample: `[
  {
    "id": "itp_4a1c",
    "folderId": "itf_902e",
    "clientTenantId": "sjs",
    "clientName": "St. Jerome School",
    "clientSlug": "sjs",
    "title": "Building A firewall — WAN failover procedure",
    "category": "Networking",
    "tags": ["firewall", "wan", "failover"],
    "isTemplate": false,
    "pinned": true,
    "linkedDeviceIds": ["dev_10ac"],
    "linkedTicketIds": [],
    "createdAt": "2026-06-02T15:10:00.000Z",
    "updatedAt": "2026-07-18T11:42:03.000Z",
    "updatedBy": "tech_maria"
  }
]`,
      sourceFile: 'routes/itdocs.js',
    },
    {
      slug: 'itdocs-create-page',
      method: 'POST',
      path: '/api/itdocs/pages',
      summary: 'Create an IT documentation page',
      description: 'Creates a page in the root tenant\'s IT documentation library. clientTenantId tags the page to a client (or "" for platform-wide); it does not move the page into that client\'s own store. Restricted to full administrators on the root tenant.',
      auth: 'session',
      requestBodyExample: `{
  "title": "Guest Wi-Fi SSID reset steps",
  "bodyMd": "## Steps\\n- Log into the controller\\n- Regenerate the guest PSK\\n- Update the front-office handout",
  "folderId": "itf_902e",
  "clientTenantId": "sjs",
  "category": "Networking",
  "tags": ["wifi", "guest-network"]
}`,
      responseExample: `{
  "id": "itp_7b3d",
  "title": "Guest Wi-Fi SSID reset steps",
  "clientTenantId": "sjs",
  "clientName": "St. Jerome School",
  "folderId": "itf_902e",
  "isTemplate": false,
  "pinned": false,
  "createdAt": "2026-07-20T09:20:11.000Z",
  "createdBy": "tech_maria"
}`,
      sourceFile: 'routes/itdocs.js',
    },
    {
      slug: 'itdocs-list-folders',
      method: 'GET',
      path: '/api/itdocs/folders',
      summary: 'List IT documentation folders',
      description: 'Returns the folder tree for the IT documentation library, sorted by order then name. Pass clientTenantId to scope to one client (or "" for platform-wide folders).',
      auth: 'session',
      params: [{ name: 'clientTenantId', in: 'query', type: 'string', description: 'Scope to one client tenant, or "" for platform-wide folders.' }],
      responseExample: `[
  { "id": "itf_902e", "name": "Networking", "parentId": "", "clientTenantId": "sjs", "order": 0, "createdAt": "2026-05-11T13:00:00.000Z", "createdBy": "admin" }
]`,
      sourceFile: 'routes/itdocs.js',
    },
    {
      slug: 'docs-tree',
      method: 'GET',
      path: '/api/docs/tree',
      summary: 'Get the document library tree',
      description: 'Returns the folders and files of the general Documentation Center (a separate virtual file system from IT documentation), filtered to what the calling user is allowed to see — admin-only folders and private files owned by other users are excluded.',
      auth: 'session',
      responseExample: `{
  "folders": [
    { "id": "df_11a", "name": "Vendor Contracts", "parentId": "", "access": "admins" }
  ],
  "files": [
    {
      "id": "dfl_902", "folderId": "df_11a", "name": "erate-contract-2026.pdf",
      "type": "application/pdf", "size": 184320, "description": "Signed E-Rate contract",
      "uploadedBy": "Maria Chen", "at": "2026-04-02T18:00:00.000Z", "image": false, "private": false, "owner": "tech_maria"
    }
  ]
}`,
      sourceFile: 'routes/docs.js',
    },
    {
      slug: 'kb-library-catalog',
      method: 'GET',
      path: '/api/articles/library',
      summary: 'List starter KB library articles',
      description: 'Returns the curated starter-article catalog (from lib/kb-library.js) with an installed flag showing whether each article already exists in this tenant\'s knowledge base (matched by libKey or title). Admin only.',
      auth: 'session',
      responseExample: `[
  { "key": "reset-student-password", "title": "How to reset a student password", "category": "Accounts", "tags": ["password", "student"], "installed": false }
]`,
      sourceFile: 'routes/kblibrary.js',
    },
    {
      slug: 'kb-library-install',
      method: 'POST',
      path: '/api/articles/library/install',
      summary: 'Install starter KB articles',
      description: 'Idempotently copies starter articles into store.articles. Selects articles via all: true, an explicit keys array, or a categories array; anything already installed (by libKey or title) is skipped rather than duplicated. Admin only.',
      auth: 'session',
      requestBodyExample: `{ "categories": ["Accounts", "Networking"] }`,
      responseExample: `{
  "installed": 6,
  "skipped": 1,
  "items": [
    { "title": "How to reset a student password", "status": "installed" },
    { "title": "Guest Wi-Fi SSID reset steps", "status": "skipped" }
  ],
  "available": 24
}`,
      sourceFile: 'routes/kblibrary.js',
    },
    {
      slug: 'kb-suggest',
      method: 'GET',
      path: '/api/articles/suggest',
      summary: 'Suggest KB articles for a query or ticket',
      description: 'Ranks store.articles against a free-text q (or, given ticketId, the ticket\'s title + description) using a local title/tags/category/body relevance score. Powers ticket-deflection suggestions; every non-empty result set increments the tenant\'s suggested counter.',
      auth: 'session',
      params: [
        { name: 'q', in: 'query', type: 'string', description: 'Free-text search terms.' },
        { name: 'ticketId', in: 'query', type: 'string', description: 'Use this ticket\'s title + description as the query instead of q. Caller must own the ticket or be admin.' },
        { name: 'limit', in: 'query', type: 'number', description: 'Max results, 1-10 (default 5).' },
      ],
      responseExample: `{
  "query": "wifi guest network down",
  "terms": ["wifi", "guest", "network"],
  "results": [
    { "id": "art_5f2a", "title": "Guest Wi-Fi SSID reset steps", "category": "Networking", "score": 13, "snippet": "Log into the controller and regenerate the guest PSK..." }
  ]
}`,
      sourceFile: 'routes/kbsuggest.js',
    },
    {
      slug: 'kb-deflect',
      method: 'POST',
      path: '/api/articles/deflect',
      summary: 'Record a KB deflection outcome',
      description: 'Records whether a suggested article resolved the user\'s issue (helpful: true), incrementing the tenant\'s deflection counters in store.meta.kbDeflect. Any authenticated user may call this.',
      auth: 'session',
      requestBodyExample: `{ "helpful": true }`,
      responseExample: `{ "ok": true, "suggested": 41, "helpful": 17 }`,
      sourceFile: 'routes/kbsuggest.js',
    },
  ],
};
